import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { modalDefaultState } from './modals';
import CreateAssessmentModal from '../components/modals/provider_modals/CreateAssessmentModal';
import CreateTemplateModal from '../components/modals/provider_modals/CreateTemplateModal';
import CreateIncidentModal from '../components/modals/provider_modals/CreateIncidentModal';
import CreateWorkspaceModal from '../components/modals/provider_modals/CreateWorkspaceModal';
import NoWorkspaceModal from '../components/modals/provider_modals/NoWorkspaceModal';
import FileUploadModal from '../components/modals/provider_modals/FileUploadModal';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCurrentUser } from './UserProvider';
import { useWorkspace } from './WorkspaceProvider';
import { searchParamKeys } from '../constants/strings';
import RecordMeetingModal from '../components/modals/provider_modals/RecordMeetingModal';
import AnnouncementModal from '../components/modals/provider_modals/AnnouncementModal';
import CustomQuickActionModal from '../components/modals/provider_modals/CustomQuickActionModal';
import TaskModal from '../components/modals/provider_modals/TaskModal';
import RoleModal from '../components/modals/provider_modals/RoleModal';
import CertificateModal from '../components/modals/provider_modals/CertificateModal';
import { useQueryParams } from '../hooks/misc';
import ObservationModal from '../components/modals/provider_modals/ObservationModal';
import ConfirmationModal from '../components/modals/provider_modals/ConfirmationModal';
import { modals } from './modals';
import { useTemplates } from './TemplateProvider';
import CreateAuditModal from '../components/modals/provider_modals/CreateAuditModal';
import CreateCPAModal from '../pages/audit/CreateCPAModal';
import VectorResponseModal from '../components/modals/provider_modals/VectorResponseModal';
import MultiFileUploadModal from '../components/modals/provider_modals/MultiFileUploadModal';
import CreateBoardModal from '../components/modals/provider_modals/CreateBoardModal';
import QRModal from '../components/modals/provider_modals/QRModal';
import OpenLinkModal from '../components/modals/provider_modals/OpenLinkModal';
import ReminderModal from '../components/modals/provider_modals/ReminderModal';
import ConditionalActionModal from '../components/modals/provider_modals/ConditionalActionModal';
import AddRiskModal from '../pages/risk_registry/AddRiskModal';
import AddRiskRegistryModal from '../pages/risk_registry/AddRiskRegistryModal';
import CheckInModal from '../components/modals/provider_modals/CheckInModal';
import FileMoveModal from '../components/modals/provider_modals/FileMoveModal';
import CompleteTaskModal from '../components/modals/provider_modals/CompleteTaskModal';
import { useOnlineStatus } from '../hooks/offline-hooks/offline-misc';
import { useLazyQuery } from '@apollo/client';
import { loader } from 'graphql.macro';
import TimecardModal from '../components/modals/provider_modals/TimecardModal';

const staleTemplateQuery = loader('./ModalProvider.template.graphql');

const ModalContext = createContext();

export const ModalProvider = ({ children }) => {
  const online = useOnlineStatus();
  const location = useLocation();
  const navigate = useNavigate();
  const { user: currentUser, allTemplateTypes } = useCurrentUser();
  const { workspaceVehicles, allWorkspaces } = useWorkspace();
  const { getParam } = useQueryParams();
  const { allTemplates, fetchTemplates } = useTemplates();

  const updateUrlParams = (params) => {
    const searchParams = new URLSearchParams(location.search);
    params.forEach(({ param, value }) => {
      searchParams.set(param, value);
      if (param === searchParamKeys.type) {
        searchParams.delete(searchParamKeys.asset);
        searchParams.delete(searchParamKeys.template);
      }
    });

    const prevQueryString = new URLSearchParams(location.search).toString();
    const nextQueryString = searchParams.toString();

    if (prevQueryString !== nextQueryString) {
      navigate(`${location.pathname}?${nextQueryString}`, { replace: true });
    }
  };

  const removeUrlParams = (params) => {
    const searchParams = new URLSearchParams(location.search);
    params.forEach(({ param }) => {
      searchParams.delete(param);
    });
    navigate(`${location.pathname}?${searchParams}`, { replace: true });
  };

  const [modalState, setModalState] = useState({
    ...modalDefaultState,
  });

  const openModal = ({ modalName, variables }) => {
    const refetchFunction = online ? fetchTemplates : () => Promise.resolve();
    refetchFunction().then(() => {
      setModalState((prevState) => ({
        ...prevState,
        [modalName]: {
          ...prevState[modalName],
          ...variables,
          show: true,
        },
      }));
      if (modalState[modalName]?.urlParams) {
        updateUrlParams([
          { param: searchParamKeys.modalName, value: modalName },
        ]);
      }
    });
  };

  const updateModal = ({ modalName, variables }) => {
    setModalState((prevState) => ({
      ...prevState,
      [modalName]: {
        ...prevState[modalName],
        ...variables,
      },
    }));
  };
  const isClosingModal = useRef(false);
  const pendingUrlModalRef = useRef(null);

  const resolveLatestTemplate = useCallback(
    (rootId) => {
      const candidates = allTemplates?.filter(
        (t) => t.parentId === rootId || t.id === rootId,
      );
      if (!candidates?.length) return null;
      return [...candidates].sort(
        (a, b) => (b.revisionNumber ?? 0) - (a.revisionNumber ?? 0),
      )[0];
    },
    [allTemplates],
  );

  const [
    fetchStaleTemplate,
    { data: staleTemplateData, error: staleTemplateError },
  ] = useLazyQuery(staleTemplateQuery);

  useEffect(() => {
    if (!pendingUrlModalRef.current) return;
    if (!staleTemplateData && !staleTemplateError) return;

    const stale = staleTemplateData?.inspectionTemplates?.[0];
    const rootId = stale?.parentId ?? stale?.id;
    const latestTemplate = rootId ? resolveLatestTemplate(rootId) : null;

    const { modalName, variables } = pendingUrlModalRef.current;
    pendingUrlModalRef.current = null;
    openModal({
      modalName,
      variables: {
        ...variables,
        ...(latestTemplate && { template: latestTemplate }),
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staleTemplateData, staleTemplateError]);

  const closeModal = ({ modalName, navigateTo }) => {
    isClosingModal.current = true;
    if (navigateTo) {
      setModalState((prevState) => ({
        ...prevState,
        [modalName]: modalDefaultState[modalName],
      }));
      navigate(navigateTo);
    } else {
      if (modalState[modalName]?.urlParams) {
        removeUrlParams(modalState[modalName].urlParams);
      }
      setModalState((prevState) => ({
        ...prevState,
        [modalName]: modalDefaultState[modalName],
      }));
    }
    setTimeout(() => {
      isClosingModal.current = false;
    }, 100);
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const modalName = searchParams.get(searchParamKeys.modalName);

    if (!modalName || !modalState[modalName]) return;
    if (isClosingModal.current) return;

    if (modalState[modalName]?.urlParams && !modalState[modalName].show) {
      const valuesFromUrlParams = modalState[modalName].urlParams.map(
        (item) => ({
          ...item,
          value: searchParams.get(item.param),
        }),
      );
      const taskIdFromUrl = searchParams.get(searchParamKeys.taskId);
      const assetFromUrl =
        workspaceVehicles?.find(
          ({ id }) => id === parseInt(searchParams.get(searchParamKeys.asset)),
        ) || null;

      const typeFromUrl = Object.values(allTemplateTypes)?.find(
        ({ key }) => key === searchParams.get(searchParamKeys.type),
      );
      const templateFromUrl = getParam(searchParamKeys.template);
      const parsedTemplateId = parseInt(templateFromUrl);
      const foundTemplate =
        allTemplates?.find(({ id }) => id === parsedTemplateId) ?? null;

      const workspaceFromUrl = getParam(searchParamKeys.workspace);
      const parsedWorkspaceId = parseInt(workspaceFromUrl);
      const foundWorkspace =
        allWorkspaces?.find(({ id }) => id === parsedWorkspaceId) ?? null;

      const variablesFromUrl = {};

      if (assetFromUrl) {
        variablesFromUrl.asset = assetFromUrl;
      }

      if (typeFromUrl) {
        variablesFromUrl.type = typeFromUrl;
      }

      if (taskIdFromUrl) {
        variablesFromUrl.taskId = taskIdFromUrl;
      }

      if (workspaceFromUrl) {
        variablesFromUrl.workspace = foundWorkspace;
      }

      if (templateFromUrl && !isNaN(parsedTemplateId)) {
        if (foundTemplate) {
          // Resolve the latest active revision: use parentId as root if it's a
          // revision, otherwise use the template's own id as root.
          const rootId = foundTemplate.parentId ?? foundTemplate.id;
          const latestTemplate = resolveLatestTemplate(rootId);
          if (latestTemplate) {
            variablesFromUrl.template = latestTemplate;
          }
        } else if (online) {
          // Template is stale (archived/deactivated). Fetch it to get parentId,
          // then resolve the latest active revision once the query completes.
          pendingUrlModalRef.current = {
            modalName,
            variables: {
              ...variablesFromUrl,
              urlParams: valuesFromUrlParams,
              participants: [currentUser],
            },
          };
          fetchStaleTemplate({ variables: { id: templateFromUrl } });
          return;
        }
        // Offline with a stale id: fall through and open the modal without a
        // pre-selected template so the user can pick one from the cached list.
      }

      openModal({
        modalName,
        variables: {
          ...variablesFromUrl,
          urlParams: valuesFromUrlParams,
          participants: [currentUser],
        },
      });
    }
    if (modalState[modalName]?.urlParams && modalState[modalName].show) {
      updateUrlParams(
        modalState[modalName].urlParams
          .map(
            ({ param, paramTarget }) =>
              modalState[modalName][param] && {
                param,
                value: paramTarget
                  ? modalState[modalName][param][paramTarget]
                  : modalState[modalName][param],
              },
          )
          .filter((p) => p),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalState, location.search, workspaceVehicles, currentUser]);

  const openConfirmationModal = (variables) => {
    setModalState((prevState) => ({
      ...prevState,
      [modals.confirmation]: {
        ...prevState[modals.confirmation],
        ...variables,
        show: true,
      },
    }));
  };
  const modalIsOpen =
    !!getParam('actionitem') ||
    Object.values(modalState).some((s) => s.show === true);

  return (
    <ModalContext.Provider
      value={{
        modalState,
        openModal,
        closeModal,
        updateModal,
        openConfirmationModal,
        modalIsOpen,
      }}
    >
      <CreateAssessmentModal />
      <CreateTemplateModal />
      <CreateIncidentModal />
      <CreateWorkspaceModal />
      <NoWorkspaceModal />
      <FileUploadModal />
      <RecordMeetingModal />
      <AnnouncementModal />
      <CustomQuickActionModal />
      <ObservationModal />
      <ConfirmationModal />
      <TaskModal />
      <CreateCPAModal />
      <CreateAuditModal />
      <RoleModal />
      <VectorResponseModal />
      <MultiFileUploadModal />
      <CertificateModal />
      <CreateBoardModal />
      <QRModal />
      <OpenLinkModal />
      <ReminderModal />
      <ConditionalActionModal />
      <AddRiskRegistryModal />
      <AddRiskModal />
      <CheckInModal />
      <FileMoveModal />
      <CompleteTaskModal />
      <TimecardModal />
      {children}
    </ModalContext.Provider>
  );
};

export const useModal = () => useContext(ModalContext);
